/**
 * نتيجة تقييم الأرض — Hero واحد بترتيب بصري مثبّت:
 *   1. Verdict
 *   2. الجملة الرئيسية
 *   3. نطاق القيمة العادلة + السعر المطلوب + شريط النطاق
 *   4. مؤشر الفرصة (حلقة) ثم الثقة في التقييم (بطاقة منفصلة)
 *   5. البطاقات الأربع
 *   6. لماذا هذه النتيجة → التحليل الكامل → اسأل نطاق
 *
 * ممنوع تبديل هذا الترتيب أو دمج مؤشر الفرصة مع الثقة.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { Button, Card, SectionTitle } from '@/components/primitives/Primitives';
import {
  ComparablesTable,
  ComponentBreakdown,
  ConfidenceCard,
  FairValueRangeBar,
  OpportunityRing,
  PriceSummary,
  ScoreComponentCard,
  VerdictChip,
} from '@/components/data/Valuation';
import { AskPanel } from '@/components/data/AskPanel';
import {
  ErrorState,
  LoadingState,
  LowConfidenceNotice,
  NoDataState,
} from '@/components/states/States';
import { UnavailablePanel } from '@/components/states/Unavailable';
import { useToast, useValuationSession } from '@/app/AppShell';
import { useWatchlist } from '@/services/market';

const LOW_CONFIDENCE_THRESHOLD = 60;

export default function ResultRoute(): JSX.Element {
  const navigate = useNavigate();
  const { status, result, retry, error } = useValuationSession();
  const watchlist = useWatchlist();
  const toast = useToast();
  const [showFull, setShowFull] = useState(false);

  if (status === 'idle') {
    return (
      <div className="nq-enter" style={{ paddingTop: 36 }}>
        <Card>
          <h1 style={{ fontSize: 20, fontWeight: 600 }}>لا توجد نتيجة بعد</h1>
          <p style={{ marginTop: 10, fontSize: 13.5, color: color.textSubtle }}>
            ابدأ من «قيّم أرض» لإدخال الحي والمساحة والسعر المطلوب.
          </p>
          <div style={{ marginTop: 18 }}>
            <Button onClick={() => navigate('/evaluate')}>قيّم أرض</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="nq-enter" style={{ paddingTop: 36 }}>
        <LoadingState />
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div className="nq-enter" style={{ paddingTop: 36 }}>
        <UnavailablePanel
          reason="coming-soon"
          title="تقييم الأرض"
          detail="محرّك نطاق القيمة العادلة لم يُطلق بعد. البيانات الرسمية المستوردة حالياً تغطي الصفقات المسجّلة فقط — ولن نعرض تقديراً مكان الرقم الحقيقي."
        />
        <div style={{ marginTop: 16 }}>
          <Button variant="ghost" onClick={() => navigate('/map')}>
            استكشف الأحياء المغطاة
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'no-data') {
    return (
      <div className="nq-enter" style={{ paddingTop: 36 }}>
        <NoDataState
          diagnosis={error?.rejection ?? null}
          onWatch={() => {
            toast('أضفنا الحي إلى المراقبة');
          }}
          onExplore={() => navigate('/map')}
        />
      </div>
    );
  }

  if (status === 'error' || !result) {
    return (
      <div className="nq-enter" style={{ paddingTop: 36 }}>
        <ErrorState onRetry={retry} onEdit={() => navigate('/evaluate')} />
      </div>
    );
  }

  const lowConfidence = result.confidence_score < LOW_CONFIDENCE_THRESHOLD;

  return (
    <div className="nq-enter" style={{ paddingTop: 36, display: 'grid', gap: 18 }}>
      {/* 1–4: Hero */}
      <Card variant="hero" as="section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <VerdictChip verdict={result.verdict} />
          {/* مستوى الدقة معلن دائماً — المستخدم يعرف على أي أساس بُني الرقم. */}
          <span
            data-testid="location-mode"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 11px',
              borderRadius: 20,
              border: `1px solid ${color.hairline}`,
              color: color.label,
              fontSize: 11.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {result.scope_label}
          </span>
        </div>

        <h1
          data-testid="hero-statement"
          style={{ marginTop: 18, fontSize: 34, fontWeight: 500, lineHeight: 1.3, letterSpacing: '-.02em' }}
        >
          {result.headline}
        </h1>

        <div style={{ marginTop: 26 }}>
          <PriceSummary
            fairLow={result.fair_value_low}
            fairHigh={result.fair_value_high}
            asking={result.asking_price}
            askingPerSqm={result.asking_price_per_sqm}
            rangeLabel={result.scope_label}
            basis={result.scope_basis}
          />
        </div>

        <div style={{ marginTop: 22 }}>
          <FairValueRangeBar
            low={result.fair_value_low}
            high={result.fair_value_high}
            asking={result.asking_price}
          />
        </div>

        {result.scope_warning ? (
          <div
            data-testid="scope-warning"
            style={{
              marginTop: 20,
              padding: '12px 14px',
              borderRadius: 12,
              background: 'rgba(240,162,75,.08)',
              border: '1px solid rgba(240,162,75,.28)',
              fontSize: 13,
              lineHeight: 1.7,
              color: color.amberText,
            }}
          >
            {result.scope_warning}
          </div>
        ) : null}

        {result.location_notice ? (
          <div
            data-testid="location-notice"
            style={{
              marginTop: 20,
              padding: '12px 14px',
              borderRadius: 12,
              background: 'rgba(240,162,75,.08)',
              border: '1px solid rgba(240,162,75,.28)',
              fontSize: 13,
              lineHeight: 1.7,
              color: color.amberText,
            }}
          >
            {result.location_notice}
          </div>
        ) : null}

        {lowConfidence ? (
          <div style={{ marginTop: 20 }}>
            <LowConfidenceNotice score={result.confidence_score} />
          </div>
        ) : null}

        <div className="nq-row" style={{ marginTop: 24, gap: 16 }}>
          <div className="nq-card nq-card--secondary" style={{ flex: '1 1 260px' }}>
            <OpportunityRing score={result.opportunity_score} />
          </div>
          <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex' }}>
            <div style={{ flex: 1 }}>
              <ConfidenceCard
                score={result.confidence_score}
                explanation={result.confidence_explanation}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* 5: البطاقات الأربع الأولى من مكوّنات المؤشر */}
      <div className="nq-row" style={{ gap: 14 }}>
        {result.score_components.slice(0, 4).map((c) => (
          <ScoreComponentCard key={c.key} component={c} />
        ))}
      </div>

      {/* 6: لماذا هذه النتيجة */}
      <Card as="section">
        <SectionTitle title="لماذا هذه النتيجة" />
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
          {result.reasons.map((r) => (
            <li key={r} style={{ fontSize: 14, lineHeight: 1.75, color: color.textBody }}>
              · {r}
            </li>
          ))}
        </ul>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setShowFull((v) => !v)}>
            {showFull ? 'إخفاء التحليل الكامل' : 'عرض التحليل الكامل'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate(`/district/${encodeURIComponent(result.district_id)}`)}
          >
            افتح صفحة الحي
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              // العيب السابق: `void` يبتلع الفشل، فتظهر رسالة نجاح حتى مع
              // رفض الخادم (401 لغير المسجّل). الآن نقول ما حدث فعلاً.
              watchlist
                .add(result.district_id)
                .then(() => toast('أضفنا الحي إلى المراقبة'))
                .catch(() => toast('المراقبة تحتاج تسجيل دخول أولاً'));
            }}
          >
            راقب الحي
          </Button>
        </div>
      </Card>

      {showFull ? (
        <Card as="section">
          <SectionTitle title="التحليل الكامل" />
          <ComponentBreakdown components={result.score_components} />
          <div style={{ marginTop: 26 }}>
            <SectionTitle title="صفقات مماثلة" />
            <ComparablesTable
              comparables={result.comparables}
              partial={result.quality.partial}
              expected={result.quality.comparablesExpected}
            />
          </div>
          <p style={{ marginTop: 16, fontSize: 10.5, color: color.micro }}>
            نسخة نموذج التقييم {result.valuation_model_version} · نسخة نموذج الثقة{' '}
            {result.confidence_model_version} ·{' '}
            {/* تسمية دقيقة: هذا متوسط عمر المقارنات المستخدمة، لا عمر بيانات
                الحي. توحيد التسميتين كان يجعلهما تبدوان متناقضتين. */}
            متوسط عمر الصفقات المقارَنة{' '}
            <span className="nq-num" dir="ltr">
              {result.data_age}
            </span>{' '}
            يوماً
          </p>
        </Card>
      ) : null}

      <AskPanel result={result} />
    </div>
  );
}
